/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, ChangeDetectorRef, Component } from '@angular/core';
import { I18nService, PermissionService } from '@dm/core';
import { ActionType, BaseComponent, eventType, infigo, Path } from '@dm/live-material-infigo';
import { FeatureConfig } from 'app/config/feature';

import { LicenseKey } from '../../../../../common/constants/license-key';
import { ToolService } from '../../../../../common/services';
import { SWITCH } from '../../../../../config';
import { GlobalSearchService } from '../../global-search.service';
import { generateTip } from '../conditionalSearch';
import { translate } from '../conditionalSearch/translateLanguage';
import { NEWFEATURE, QUICKSTARTJSON, RECOMMENDED } from '../const';
import type * as DefaultData from './init-status-default-data';

@Component({
    selector: 'app-init-status',
    templateUrl: './init-status.component.html',
    styleUrls: ['./init-status.component.scss'],
})
export class InitStatusComponent extends BaseComponent implements OnInit {
    mapData = {
        latestList: [Path.history, (value: any[]): any[] => value.map((item) => {
            const v = generateTip(item.title);

            if (typeof v === 'string') {
                return { ...item, subTitle: ` ${v}` };
            }

            return item;
        })],
        collectList: [Path.collect, (value: any[]): any[] => value.map((item) => {
            const v = generateTip(item.title);
            if (typeof v === 'string') {
                return { ...item, subTitle: ` ${v}` };
            }

            return item;
        })],
    };
    recommendConfig: DefaultData.SearchListConfig = {
        type: 'recommend',
        icon: '#app-icon-search-recommend',
        title: this.i18n.get('home.search_term_recommendSearch_lab'),
    };
    recommendList: DefaultData.SearchDataConfig[] = [];
    commonConfig: DefaultData.SearchListConfig = {
        type: 'common',
        icon: '#app-icon-search-common',
        title: this.i18n.get('home.search_term_mostVisitedPage_lab'),
        key: 'name',
    };
    commonList: DefaultData.SearchDataConfig[] = [];
    latestConfig: DefaultData.SearchListConfig = {
        type: 'latest',
        icon: '#app-icon-search-latest',
        title: this.i18n.get('home.search_term_recentSearch_lab'),
        operation: {
            tooltip: this.i18n.get('home.search_term_searchContent_info'),
        },
    };
    latestList: DefaultData.SearchDataConfig[];
    collectedConfig: DefaultData.SearchListConfig = {
        type: 'collected',
        icon: '#infigo-collected',
        title: this.i18n.get('home.search_term_favorSearch_lab'),
        operation: {
            tooltip: this.i18n.get('home.search_term_cancelFavor_lab'),
        },
    };
    collectList: DefaultData.SearchDataConfig[];
    newFeatureList = [];
    quickStartList = [];
    detach = false;
    isOem = this.globalSearchService.isOem;
    isProdRelease = this.globalSearchService.isProdRelease;
    SWITCH = SWITCH;
    FeatureConfig = FeatureConfig;
    supportSmartThin = this.toolService.supportSmartThinLicense();

    constructor(public globalSearchService: GlobalSearchService,
        public changeDetectorRef: ChangeDetectorRef,
        public i18n: I18nService,
        public toolService: ToolService,
        public permissionService: PermissionService) {
        super();
        globalSearchService.getDefaultShowData();
        this.commonList = globalSearchService.defaultSearchData.advancedPages;
    }

    handleTranslate(arr: any[]): any[] {
        arr.forEach((i) => {
            i.title = translate(i.title, this.i18n.language);
        });
        return arr;
    }

    ngOnInit(): void {
        super.ngOnInit();
        const history = this.handleTranslate(infigo.history.getHistory());
        const collect = this.handleTranslate(infigo.collect.getCollect());
        infigo.store.dispatch({
            type: ActionType.UpdateHistory,
            value: history,
        });
        infigo.history.setHistory(history);
        infigo.collect.setCollect(collect);
        infigo.store.dispatch({
            type: ActionType.UpdateCollect,
            value: collect,
        });
        if (!FeatureConfig.IsSupportOpenQuickStart) {
            this.quickStartList = QUICKSTARTJSON(this.i18n.language, FeatureConfig.IsHighProdQuickStartList)
                .filter((element) => !element.hide);
            if (!this.supportSmartThin) {
                this.quickStartList = this.quickStartList.filter((element) => element.index !== 0);
            }
            this.newFeatureList = this.handleLicense(NEWFEATURE[this.i18n.language]);
        } else {
            this.quickStartList = QUICKSTARTJSON(this.i18n.language, FeatureConfig.IsHighProdQuickStartList)
                .filter((element) => !element.hide);
            if (!this.supportSmartThin) {
                this.quickStartList = this.quickStartList.filter((element) => element.index !== 0);
            }
            const newfeature = NEWFEATURE[this.i18n.language].filter((item) => item && !item.hide);
            this.newFeatureList = this.handleLicense(newfeature);
        }

        this.recommendList = this.handleLicense(RECOMMENDED[this.i18n.language === 'ja' ? 'en' : this.i18n.language]);
    }

    handleLicense(list: any[]): any[] {
        let newList: any[];
        newList = list.filter(
            (element) => element &&
                this.permissionService.checkShow(element.permission, element.license) &&
                (!element.isNASFeature || SWITCH.SUPPORT_NAS) &&
                (!element.noOem || !this.isOem) &&
                (!element.noProdRelease || !this.isProdRelease) &&
                !element.isNotSupportFeatureInOceanMicro,
        );
        return newList;
    }

    handleSearchObject(event: { title: any }): void {
        infigo.store.dispatch({
            type: ActionType.UserInput,
            value: event.title,
        });
    }

    removeCollectedItem(item: { timestamp: any }): void {
        infigo.store.dispatch({
            type: ActionType.UpdateCollect,
            value: infigo.collect.remove(item.timestamp),
        });
    }

    handleJumpToPage($event: any): void {
        infigo.dispatch(eventType.CLICK_LIST_ITEM, $event);
    }

    // Unix用户 Win用户无跳转功能
    handleSelectedResource(selectedType: string | number): void {
        this.globalSearchService.operationsData.find((item) => {
            if (item.type === selectedType) {
                infigo.dispatch(eventType.CLICK_LIST_ITEM, item);
            }
        });
    }

    checkShowNAS(): boolean {
        return SWITCH.SUPPORT_NAS;
    }

    checkShowSAN(): boolean {
        return FeatureConfig.IsReplaceStrLunWithNamespace || this.permissionService.isLicenseExist([LicenseKey.SmartThin]);
    }
}
